import React, { useEffect, useRef, useState } from 'react';
import { AdminDeckDirection, AdminDeckError, AdminDeckOptions, AdminDeckResponse, AdminDeckType, gameApi } from '../api';
import { MythosBreakdown } from './MythosBreakdown';
import { Encounter } from './wiki/Encounter';
import './AdminDeckPanel.css';

interface Props {
  gameId: string;
  refreshVersion: number;
  onCheckpointChanged: () => void;
  onRefreshGameState?: () => void | Promise<void>;
}

export const AdminDeckPanel: React.FC<Props> = ({ gameId, refreshVersion, onCheckpointChanged, onRefreshGameState }) => {
  const [type, setType] = useState<AdminDeckType>('Mythos');
  const [location, setLocation] = useState('');
  const [options, setOptions] = useState<AdminDeckOptions | null>(null);
  const [deck, setDeck] = useState<{ key: string; data: AdminDeckResponse } | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [optionsReload, setOptionsReload] = useState(0);
  const mounted = useRef(false);
  const requestSequence = useRef(0);
  const currentGame = useRef(gameId);
  currentGame.current = gameId;
  const key = JSON.stringify([gameId, type, location, refreshVersion]);
  const selection = useRef(key);
  selection.current = key;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);
    setOptions(previous => previous?.gameId === gameId ? previous : null);
    gameApi.getAdminDeckOptions(gameId).then(data => {
      if (!cancelled && currentGame.current === gameId) setOptions(data);
    }).catch(err => {
      if (!cancelled && currentGame.current === gameId) {
        setOptions(null);
        setError(err instanceof Error ? err.message : 'Failed to load deck locations');
      }
    }).finally(() => {
      if (!cancelled && currentGame.current === gameId) setOptionsLoading(false);
    });
    return () => { cancelled = true; };
  }, [gameId, refreshVersion, optionsReload]);

  useEffect(() => {
    if (!options || options.gameId !== gameId) return;
    const locations = type === 'ArkhamEncounter' ? options?.arkhamLocations : options?.otherWorldLocations;
    setLocation(previous => type === 'Mythos' ? '' : locations?.includes(previous) ? previous : locations?.[0] ?? '');
  }, [gameId, type, options]);

  useEffect(() => {
    requestSequence.current++;
    setDeck(null);
    setLoading(false);
    setSaving(false);
    setError(null);
    setSuccess(null);
  }, [gameId, type, location, refreshVersion]);

  const locations = type === 'ArkhamEncounter' ? options?.arkhamLocations ?? [] : options?.otherWorldLocations ?? [];
  const validSelection = !optionsLoading && options?.gameId === gameId && (type === 'Mythos' || locations.includes(location));
  const busy = optionsLoading || loading || saving;
  const loaded = deck?.key === key ? deck.data : null;

  const loadDeck = async (clearMessages = true) => {
    if (!validSelection) return;
    const selectedKey = key;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setDeck(null);
    if (clearMessages) { setError(null); setSuccess(null); }
    try {
      const data = await gameApi.getAdminDeck(gameId, type, type === 'Mythos' ? undefined : location);
      if (mounted.current && selection.current === selectedKey && sequence === requestSequence.current) {
        setDeck({ key: selectedKey, data });
      }
    } catch (err) {
      if (mounted.current && selection.current === selectedKey && sequence === requestSequence.current) {
        setError(err instanceof Error ? err.message : 'Failed to load deck');
      }
    } finally {
      if (mounted.current && selection.current === selectedKey && sequence === requestSequence.current) setLoading(false);
    }
  };

  const move = async (deckIndex: number, direction: AdminDeckDirection) => {
    if (!loaded || busy) return;
    const selectedKey = key;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await gameApi.moveAdminDeckCard(gameId, type, {
        expectedStateRevision: loaded.stateRevision, deckIndex, direction,
        ...(type === 'Mythos' ? {} : { location }),
      });
      if (!mounted.current || selection.current !== selectedKey) return;
      setSuccess(result.message);
      onCheckpointChanged();
      await loadDeck(false);
      if (mounted.current && selection.current === selectedKey) await onRefreshGameState?.();
    } catch (err) {
      if (!mounted.current || selection.current !== selectedKey) return;
      setError(err instanceof Error ? err.message : 'Failed to move card');
      if (err instanceof AdminDeckError && err.status === 409) await loadDeck(false);
    } finally {
      if (mounted.current && selection.current === selectedKey) setSaving(false);
    }
  };

  return <section className="admin-deck-panel">
    <h4>Deck Order</h4>
    <div className="debug-grant-form">
      <label>Deck:
        <select value={type} onChange={event => setType(event.target.value as AdminDeckType)} disabled={busy}>
          <option value="Mythos">Mythos</option>
          <option value="ArkhamEncounter">Arkham Encounters</option>
          <option value="OtherWorldEncounter">Other World Encounters</option>
        </select>
      </label>
      {type !== 'Mythos' && <label>Encounter location:
        <select value={location} onChange={event => setLocation(event.target.value)} disabled={busy || !locations.length}>
          {!locations.length && <option value="">No locations available</option>}
          {locations.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
      </label>}
      <button onClick={() => loadDeck()} disabled={busy || !validSelection}>{loading ? 'Loading…' : 'Load Deck'}</button>
      {!options && !optionsLoading && <button onClick={() => setOptionsReload(value => value + 1)}>Reload Locations</button>}
    </div>
    {optionsLoading && <p>Loading deck locations…</p>}
    {error && <p className="error" role="alert">{error}</p>}
    {success && <p role="status">{success}</p>}
    {loaded && <>
      <p>{loaded.cards.length} cards · Top card first</p>
      {type === 'OtherWorldEncounter' && <p>Shared deck · Encounter text for {location}</p>}
      {!loaded.cards.length && <p>This deck is empty.</p>}
      <ol className="admin-deck-list">
        {loaded.cards.map((card, index) => <li className="admin-deck-entry" key={`${loaded.stateRevision}-${card.deckIndex}`}>
          <div className="admin-deck-card">
            <div className="admin-deck-metadata">{index + 1}{index === 0 ? ' · Top' : ''} · {card.id || 'No ID'} · {card.expansion}{card.color ? ` · ${card.color}` : ''}</div>
            {type === 'Mythos' && card.mythosDetails ? <MythosBreakdown mythosDetails={card.mythosDetails} />
              : card.text ? <Encounter location={location} text={card.text} /> : <p>No encounter text for this location.</p>}
          </div>
          <div className="admin-deck-moves">
            <button aria-label={`Move card ${index + 1} up`} onClick={() => move(card.deckIndex, 'Up')} disabled={busy || index === 0}>Up</button>
            <button aria-label={`Move card ${index + 1} down`} onClick={() => move(card.deckIndex, 'Down')} disabled={busy || index === loaded.cards.length - 1}>Down</button>
            <button aria-label={`Move card ${index + 1} to top`} onClick={() => move(card.deckIndex, 'Top')} disabled={busy || index === 0}>Move to Top</button>
          </div>
        </li>)}
      </ol>
    </>}
  </section>;
};
